import { createContext, useContext } from 'react';

/**
 * The signed-in user and the operations that change that state, independent of
 * which identity provider produced the session.
 */
export interface AuthSession {
    isAuthenticated: boolean;
    /**
     * True while the provider is still establishing the session, or an
     * interaction with it is under way. Until it clears, `isAuthenticated`
     * is not yet the answer, and starting a sign-in would race the one the
     * provider is settling.
     */
    isPending: boolean;
    /** Display name of the signed-in user, or undefined when unknown. */
    userName: string | undefined;
    /**
     * Sign-in name of the user — an email address or UPN — or undefined when
     * the provider does not supply one.
     */
    userSignInName: string | undefined;
    logIn: () => Promise<void>;
    /**
     * Signs in again with the provider asked to let the user choose the
     * account, rather than reusing the session it already holds. The session
     * signed in now stays in place until the new sign-in replaces it.
     */
    switchAccount: () => Promise<void>;
    logOut: () => Promise<void>;
}

/**
 * The session seen when no provider is mounted, i.e. when authentication is
 * switched off. Deliberately unauthenticated: a missing provider must never
 * read as a signed-in user.
 */
export const UNAUTHENTICATED_SESSION: AuthSession = {
    isAuthenticated: false,
    isPending: false,
    userName: undefined,
    userSignInName: undefined,
    logIn: async () => undefined,
    switchAccount: async () => undefined,
    logOut: async () => undefined,
};

const AuthSessionContext = createContext<AuthSession>(UNAUTHENTICATED_SESSION);

export default AuthSessionContext;

/** The current session, from whichever identity provider is configured. */
export const useAuthSession = (): AuthSession => useContext(AuthSessionContext);
